import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

/**
 * "Vad Finn vet" — the memory trust surface (proxied by the backend to the agents gateway).
 *
 * Shapes (gateway contract, passed through verbatim):
 *   memory: { id, scope: 'company'|'user', kind: 'fact'|'preference'|'pattern'|'correction',
 *             subject, displayText, source, verificationStatus: 'confirmed'|'inferred'|'contradicted',
 *             createdAt, lastUsedAt?, version, chainId }
 *   proposal: { id, displayText, state: 'pending'|'confirmed'|'rejected', origin, createdAt }
 *   profile: { profile: { version, status, facts: [{key, value, source, sourceRef}], summary,
 *                         composedAt, verifiedAt?, verifiedByUserId?, ... } }
 *
 * Reads are member-level. Writes are enforced per row by the gateway (own user-scoped rows are
 * always yours; company-scoped rows need an admin role).
 */
export const listMemories = (companyId) =>
    axios.get(getApiUrl(`${companyId}/agent-memory`), { withCredentials: true });

export const listProposals = (companyId) =>
    axios.get(getApiUrl(`${companyId}/agent-memory/proposals`), { withCredentials: true });

export const confirmProposal = (companyId, proposalId) =>
    axios.post(getApiUrl(`${companyId}/agent-memory/proposals/${proposalId}/confirm`), {}, { withCredentials: true });

export const rejectProposal = (companyId, proposalId) =>
    axios.post(getApiUrl(`${companyId}/agent-memory/proposals/${proposalId}/reject`), {}, { withCredentials: true });

/**
 * Build the correction claim the gateway's `memoryClaimSchema` requires. The
 * UI edits free text, so the corrected value is `{ text }` — carrying the old
 * structured valueJson forward would preserve possibly-wrong facts under a
 * corrected sentence, and the superseded version keeps them in history anyway.
 * Scope/subject come from the memory being corrected; the schema refuses a
 * correction that names no supersedesId or tries to change scope.
 */
export const buildCorrectionClaim = (memory, text) => ({
    scope: memory.scope,
    kind: 'correction',
    subject: memory.subject,
    valueJson: { text },
    displayText: text,
    source: 'user',
    supersedesId: memory.id,
});

export const correctMemory = (companyId, memoryId, claim) =>
    axios.post(getApiUrl(`${companyId}/agent-memory/${memoryId}/correct`), { claim }, { withCredentials: true });

export const forgetMemory = (companyId, memoryId) =>
    axios.delete(getApiUrl(`${companyId}/agent-memory/${memoryId}`), { withCredentials: true });

export const getAgentProfile = (companyId) =>
    axios.get(getApiUrl(`${companyId}/agent-memory/profile`), { withCredentials: true });

// Stamps the named draft version as reviewed; the facts card and prompt-render gate both
// key on a CONFIRMED profile, and nothing confirmed one from the product before this.
/**
 * Build (or refresh) the company profile from the backend's own facts.
 *
 * The step that had no caller: everything downstream — reading, confirming, rendering the facts
 * into an agent's prompt — assumed a profile existed, and nothing in the product ever made one.
 */
export const rebuildAgentProfile = (companyId) =>
    axios.post(getApiUrl(`${companyId}/agent-memory/profile/rebuild`), {}, { withCredentials: true });

export const confirmAgentProfile = (companyId, version) =>
    axios.post(getApiUrl(`${companyId}/agent-memory/profile/confirm`), { version }, { withCredentials: true });
